import { type FC, type MouseEvent, useContext } from 'react';
import { getAccessibleTabIndex } from '../../Button/ariaHelpers';
import { Icon } from '../../Icon/Icon';
import {
  getLinkInnerClassNames,
  linkSpinnerWrapperClassNames,
} from '../../Link/Link.styles';
import { fromInteractiveAtomToIconSize } from '../../utils/InteractiveAtomProps';
import { SegmentButtonContext } from '../SegmentButton.context';
import { SegmentedButtonContext } from '../SegmentedButton.context';
import type { SegmentButtonProps } from './SegmentButton.types';
import { getSegmentButtonClassNames } from './SegmentedButton.styles';

export const SegmentButton: FC<SegmentButtonProps> = ({
  iconPosition = 'start',
  ...props
}) => {
  const position = useContext(SegmentButtonContext);
  const {
    selectedButtons,
    size = 'default',
    variant = 'outline',
    onChange,
  } = useContext(SegmentedButtonContext);

  const buttonDisabled = props.loading || props.disabled;

  let icon = <></>;

  const handleClick = (_e: MouseEvent<HTMLElement>) => {
    if (onChange) {
      onChange(props.value);
    }
  };

  if (props.icon) {
    icon = (
      <Icon
        glyph={props.icon}
        size={fromInteractiveAtomToIconSize[size]}
        iconStyle={props.iconStyle}
      />
    );
  }

  const getLoadingInnerContent = () => (
    <span className={linkSpinnerWrapperClassNames}>
      <Icon
        glyph='fa-spinner'
        loading
        size={fromInteractiveAtomToIconSize[size]}
      />
    </span>
  );

  const getInnerContent = () => (
    <span className={getLinkInnerClassNames(props.loading)}>
      {iconPosition === 'start' && icon}
      {props.children && <span className='px-1'>{props.children}</span>}
      {iconPosition === 'end' && icon}
    </span>
  );

  return (
    <button
      type='button'
      className={getSegmentButtonClassNames(
        props.className,
        variant,
        size,
        position,
        selectedButtons.includes(props.value),
        buttonDisabled ?? false,
      )}
      disabled={buttonDisabled}
      onClick={handleClick}
      tabIndex={getAccessibleTabIndex(buttonDisabled)}
    >
      {getInnerContent()}
      {props.loading && getLoadingInnerContent()}
    </button>
  );
};
